import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig} from '@chakra-ui/react'
import YoutubeIframe from '../../../youtube-video'
import useDamImage from '../../../../custom-hooks/use-dam-image'

const PromoVideoSlideCard = ({
    label,
    title,
    source,
    thumbnail,
    isVariantStyle = false,
    isPlaying,
    setIsPlaying,
    contentType = '',
}) => {
    const styles = useStyleConfig('PromoSlider', {
        variant: isVariantStyle ? 'paperlessVariant' : '',
    })
    const [interacting, setInteracting] = useState(false)
    const videoContainerRef = useRef(null)
    const overlayRef = useRef(null)

    const pdpThumbnailUrl = thumbnail?.url
    const {url: damThumbnailUrl} = useDamImage(thumbnail)

    const thumbnailUrl = pdpThumbnailUrl || damThumbnailUrl
    const youtubeKey = source?.href || source?.title

    const handleInteractionStart = () => {
        setInteracting(true)
    }

    const handleInteractionEnd = () => {
        setInteracting(false)
        setIsPlaying(false)
    }

    useEffect(() => {
        const handleKeyDown = (e) => {
            const activeElement = document.activeElement
            const wrapper = videoContainerRef.current

            if (activeElement === wrapper && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                e.stopPropagation()

                const thumbnailButton = videoContainerRef.current?.querySelector(
                    '[data-testid="video-container"] button',
                )
                if (thumbnailButton) {
                    thumbnailButton.click()
                    handleInteractionStart()
                } else {
                    const iframe = videoContainerRef.current?.querySelector('iframe')
                    if (iframe) {
                        iframe.setAttribute('tabindex', '0')
                        iframe.focus()

                        iframe.click()
                    }
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown, true)
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true)
        }
    }, [])

    return (
        <Box
            ref={videoContainerRef}
            {...styles.promoSliderVideoContainer}
            position="relative"
            data-testid="promo-slide-video-container"
        >
            {source?.href && (
                <YoutubeIframe
                    videoSrc={source.href}
                    title={source?.title}
                    height="100%"
                    thumbnail={thumbnailUrl}
                    onPause={handleInteractionEnd} // Reset interaction on pause
                    onPlay={() => setIsPlaying(true)}
                    enableInteractionHandlers={true}
                    key={youtubeKey}
                    isVariantStyle={isVariantStyle}
                    contentType={contentType}
                />
            )}

            <Box
                ref={overlayRef}
                {...styles.videoContainerYoutube}
                pointerEvents={interacting ? 'none' : 'auto'} // Allow interaction with the video when interacting
                onClick={handleInteractionStart} // Set interacting to true on click
            />

            {!isPlaying && (
                <>
                    <Box
                        display={{base: 'flex', lg: 'none'}}
                        flexDirection="column"
                        alignItems="flex-start"
                        position="absolute"
                        bottom={4}
                        left={4}
                        zIndex={1}
                        data-testid="promo-slide-video-content-mobile"
                    >
                        {label && <Text {...styles.label}>{label}</Text>}
                        {title && <Text {...styles.title}>{title}</Text>}
                    </Box>
                    <Box {...styles.contentOverlay} data-testid="promo-slide-video-content">
                        {label && <Text {...styles.label}>{label}</Text>}
                        {title && <Text {...styles.title}>{title}</Text>}
                    </Box>
                </>
            )}
        </Box>
    )
}

PromoVideoSlideCard.propTypes = {
    label: PropTypes.string,
    title: PropTypes.string,
    source: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
    thumbnail: PropTypes.object,
    isVariantStyle: PropTypes.bool,
    isPlaying: PropTypes.bool,
    setIsPlaying: PropTypes.func,
    contentType: PropTypes.string,
}
export default PromoVideoSlideCard
